import React from 'react';

/**
 * The Documents sidebar's glyphs, drawn exactly as on the "Sidebar — row
 * states" artboard (card 1.6): inline stroke SVG, 1.6 stroke, round caps, no
 * emoji. Colour comes from `currentColor`, so the CSS decides the tint (the
 * accent only for an open folder and the active row).
 */
function Glyph({ size = 16, className = '', strokeWidth = 1.6, children }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

const PAGE = 'M6 3h8l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z';
const FOLDER = 'M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z';

export const PageGlyph = (p) => (
  <Glyph {...p}>
    <path d={PAGE} />
    <path d="M14 3v5h5" />
  </Glyph>
);

export const FolderGlyph = (p) => (
  <Glyph {...p}>
    <path d={FOLDER} />
  </Glyph>
);

/** An expanded folder: the closed shape plus the lid line. */
export const FolderOpenGlyph = (p) => (
  <Glyph {...p}>
    <path d={FOLDER} />
    <path d="M3 11h18" />
  </Glyph>
);

export const NewPageGlyph = (p) => (
  <Glyph {...p}>
    <path d={PAGE} />
    <path d="M14 3v5h5M12 11v6M9 14h6" />
  </Glyph>
);

export const NewFolderGlyph = (p) => (
  <Glyph {...p}>
    <path d={FOLDER} />
    <path d="M12 11v6M9 14h6" />
  </Glyph>
);

export const ChevronGlyph = (p) => (
  <Glyph strokeWidth={2} {...p}>
    <path d="m9 6 6 6-6 6" />
  </Glyph>
);

export const ChevronDownGlyph = (p) => (
  <Glyph {...p}>
    <path d="m7 10 5 5 5-5" />
  </Glyph>
);

export const PlusGlyph = (p) => (
  <Glyph {...p}>
    <path d="M12 5v14M5 12h14" />
  </Glyph>
);

export const MoreGlyph = (p) => (
  <Glyph {...p}>
    <circle cx="5" cy="12" r="1.2" />
    <circle cx="12" cy="12" r="1.2" />
    <circle cx="19" cy="12" r="1.2" />
  </Glyph>
);

export const SearchGlyph = (p) => (
  <Glyph {...p}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m20 20-4-4" />
  </Glyph>
);

export const CloseGlyph = (p) => (
  <Glyph {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Glyph>
);

/* Row menu (card 3.3), as drawn on the artboard. */
export const RenameGlyph = (p) => (
  <Glyph {...p}>
    <path d="M4 20h4l10-10-4-4L4 16z" />
  </Glyph>
);

export const DuplicateGlyph = (p) => (
  <Glyph {...p}>
    <rect x="8" y="8" width="12" height="12" rx="2" />
    <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
  </Glyph>
);

export const PinGlyph = (p) => (
  <Glyph {...p}>
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
    <circle cx="12" cy="12" r="4" />
  </Glyph>
);

export const LinkGlyph = (p) => (
  <Glyph {...p}>
    <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1.5 1.5" />
    <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1.5-1.5" />
  </Glyph>
);

export const ExportGlyph = (p) => (
  <Glyph {...p}>
    <path d="M12 4v10M8 10l4 4 4-4" />
    <path d="M4 18h16" />
  </Glyph>
);

export const TrashGlyph = (p) => (
  <Glyph {...p}>
    <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
  </Glyph>
);

export const PrintGlyph = (p) => (
  <Glyph {...p}>
    <path d="M7 9V3h10v6" />
    <rect x="4" y="9" width="16" height="8" rx="2" />
    <path d="M7 14h10v7H7z" />
  </Glyph>
);

export const CloudGlyph = (p) => (
  <Glyph {...p}>
    <path d="M7 18a4 4 0 0 1-.5-7.97A6 6 0 0 1 18 9a4.5 4.5 0 0 1-.5 9z" />
  </Glyph>
);

export const DeviceGlyph = (p) => (
  <Glyph {...p}>
    <rect x="3" y="4" width="18" height="12" rx="2" />
    <path d="M8 20h8M12 16v4" />
  </Glyph>
);
